import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  Eye,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import SolicitudDetalle from '../../components/interno/SolicitudDetalle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PanelInterno = () => {
  const { getAuthHeaders } = useAuth();
  const [estadisticas, setEstadisticas] = useState(null);
  const [solicitudesSocios, setSolicitudesSocios] = useState([]);
  const [solicitudesGuia, setSolicitudesGuia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, sociosRes, guiaRes] = await Promise.all([
        axios.get(`${API_URL}/api/interno/estadisticas`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/interno/solicitudes/socios?estado=${filtroEstado}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/interno/solicitudes/guia?estado=${filtroEstado}`, { headers: getAuthHeaders() })
      ]);
      
      setEstadisticas(statsRes.data);
      setSolicitudesSocios(sociosRes.data.solicitudes);
      setSolicitudesGuia(guiaRes.data.solicitudes);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('No tiene permisos para acceder al panel interno');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filtroEstado]);

  const handleVerDetalle = (solicitud, tipo) => {
    setSolicitudSeleccionada(solicitud);
    setTipoSeleccionado(tipo);
  };

  const handleCerrarDetalle = () => {
    setSolicitudSeleccionada(null);
    setTipoSeleccionado(null);
    fetchData(); // Refrescar datos
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      pendiente: 'bg-yellow-100 text-yellow-700',
      aprobado: 'bg-green-100 text-green-700',
      rechazado: 'bg-red-100 text-red-700',
      devuelto: 'bg-orange-100 text-orange-700'
    };
    const iconos = {
      pendiente: Clock,
      aprobado: CheckCircle2,
      rechazado: XCircle,
      devuelto: RotateCcw
    };
    const Icono = iconos[estado] || Clock;
    
    return (
      <Badge className={`${estilos[estado] || estilos.pendiente} flex items-center gap-1`}>
        <Icono className="w-3 h-3" />
        {estado.charAt(0).toUpperCase() + estado.slice(1)}
      </Badge>
    );
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (solicitudSeleccionada) {
    return (
      <SolicitudDetalle
        solicitud={solicitudSeleccionada}
        tipo={tipoSeleccionado}
        onClose={handleCerrarDetalle}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="panel-interno">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1E3876]">Panel Interno CAO</h1>
        <p className="text-gray-600">Gestión de solicitudes de socios y guía de profesionales</p>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="cao-card border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pendientes</p>
                    <p className="text-2xl font-bold text-[#1E3876]">
                      {estadisticas.socios.pendientes + estadisticas.guia.pendientes}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="cao-card border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Aprobados</p>
                    <p className="text-2xl font-bold text-[#1E3876]">
                      {estadisticas.socios.aprobados + estadisticas.guia.aprobados}
                    </p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="cao-card border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Devueltos</p>
                    <p className="text-2xl font-bold text-[#1E3876]">
                      {estadisticas.socios.devueltos + estadisticas.guia.devueltos}
                    </p>
                  </div>
                  <RotateCcw className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="cao-card border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rechazados</p>
                    <p className="text-2xl font-bold text-[#1E3876]">
                      {estadisticas.socios.rechazados + estadisticas.guia.rechazados}
                    </p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Filtros de estado */}
      <div className="flex gap-2 flex-wrap">
        {['pendiente', 'aprobado', 'devuelto', 'rechazado'].map((estado) => (
          <Button
            key={estado}
            variant={filtroEstado === estado ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFiltroEstado(estado)}
            className={filtroEstado === estado ? 'bg-[#2853B9]' : ''}
          >
            {estado.charAt(0).toUpperCase() + estado.slice(1)}
          </Button>
        ))}
      </div>

      {/* Tabs de solicitudes */}
      <Tabs defaultValue="socios" className="space-y-4">
        <TabsList>
          <TabsTrigger value="socios" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Alta de Socios ({solicitudesSocios.length})
          </TabsTrigger>
          <TabsTrigger value="guia" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Guía Profesionales ({solicitudesGuia.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="socios">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
            </div>
          ) : solicitudesSocios.length === 0 ? (
            <Card className="cao-card">
              <CardContent className="p-8 text-center text-gray-500">
                No hay solicitudes con estado "{filtroEstado}"
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {solicitudesSocios.map((solicitud) => (
                <motion.div
                  key={solicitud.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="cao-card hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleVerDetalle(solicitud, 'socios')}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-[#1E3876]">
                              {solicitud.user_nombre || 'Sin nombre'}
                            </h3>
                            {getEstadoBadge(solicitud.estado)}
                          </div>
                          <div className="text-sm text-gray-500 space-y-1">
                            <p>Email: {solicitud.user_email}</p>
                            <p>Categoría: {solicitud.datos?.categoria_socio?.replace(/_/g, ' ')}</p>
                            <p>Fecha: {formatFecha(solicitud.created_at)}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="guia">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
            </div>
          ) : solicitudesGuia.length === 0 ? (
            <Card className="cao-card">
              <CardContent className="p-8 text-center text-gray-500">
                No hay solicitudes con estado "{filtroEstado}"
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {solicitudesGuia.map((solicitud) => (
                <motion.div
                  key={solicitud.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="cao-card hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleVerDetalle(solicitud, 'guia')}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-[#1E3876]">
                              {solicitud.datos?.nombre_apellido || solicitud.user_nombre}
                            </h3>
                            {getEstadoBadge(solicitud.estado)}
                          </div>
                          <div className="text-sm text-gray-500 space-y-1">
                            <p>Email: {solicitud.user_email}</p>
                            <p>Subespecialidad: {solicitud.datos?.subespecialidad_principal}</p>
                            <p>Fecha: {formatFecha(solicitud.created_at)}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PanelInterno;
